import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from '../routing';
import { revenueApi, formatCurrency, formatCurrencyWhole } from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import { currentMonthRange, periodLabel, PRESETS_ANALYTIC, activePresetKey, readPeriodParams, withPeriodParams } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import { formatIsoMonth, entryFrontierLine, isoToday } from '../utils/revenueFormat';
import { useSortable } from '../utils/useSortable';
import { useIsMobile } from '../utils/useIsMobile';
import { track, EVENTS } from '../utils/analytics';
import Icon from '../components/Icon';
import RevenueCalculationExplanation from '../components/RevenueCalculationExplanation';
import FoodCostIcon from '../components/FoodCostIcon';
import ExpandCaret from '../components/ExpandCaret';
import PeriodPicker from '../components/PeriodPicker';
import ExportMenu from '../components/ExportMenu';
import InlineLoadError from '../components/InlineLoadError';
import EmptyState from '../components/EmptyState';
import RevenueMetricTiles, { ExcludedSpendNote, EntryFrontierText } from '../components/RevenueMetricTiles';
import HqOnlyNotice from '../components/HqOnlyNotice';
import { ViewAsMenuItems } from '../components/ViewAsMenu';

// הכנסות מול הוצאות — the full table behind the /analysis card.
//
// The third of the report family (רכש לפי ספקים, רכש לפי סיווג פריטים), but
// STRUCTURALLY DIFFERENT from both, and deliberately so: those tables carry 15
// to 130 rows, this one carries the restaurant's 3-10 קטגוריות. Filtering a
// four-row table adds nothing, so there is no filter bar, no search, no
// MultiSelectFilter — the period picker IS the control. What the page adds over
// the card is DEPTH, not narrowing:
//
//   • the per-line fields the card computes and never shows (coverage, the
//     net twins)
//   • a monthly series — a cost ratio in isolation says nothing; "35%, same as
//     last month" is the sentence someone can act on
//   • the suppliers behind each line
//   • the target expressed as a share of revenue, so "your budget assumed 30%,
//     you are at 35%" is one glance rather than two screens and a division
//
// EVERYTHING IS NET (ללא מע״מ). Revenue is only ever entered gross — it is the
// Z-report figure — so revenue_net is an ESTIMATE at the current VAT rate and
// says so in the header. The percentages are unaffected either way: both sides
// carry the same rate, so it cancels.
//
// הוצאות = COUNTED DAYS ONLY (owner, 2026-09-22): every spend figure on this
// page — tiles, categories, months, suppliers — is purchasing on the days of the
// picked range that have revenue entered or are marked closed (countedDays in
// server/lib/revenue.js). What was left out is in the ⋮ menu's חשוב לדעת.
//
// NO PER-DAY ROWS. Revenue is entered for a range and spread evenly, so a daily
// figure is an artefact of the spreading rather than something that happened.
// The server refuses to emit them; this page must not invent them.

// Every colSpan derives from here — hand-written ones drift from the header,
// and a row one cell short reads as a rendering glitch rather than a bug.
const COLS = 3;

const pct = (v) => (v == null ? '-' : `${v}%`);


const SOURCE_HE = { classification: 'סיווג פריט', supplier: 'ספק', mixed: 'מעורב' };
const sourceLabel = (s) => SOURCE_HE[s] || 'ללא שיוך';

export default function RevenueVsExpensesReportPage() {
  const { budgetModuleEnabled } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // `?period=<key>` for a preset, re-evaluated to today (period.js, withPeriodParams).
  const urlRange = readPeriodParams(searchParams, PRESETS_ANALYTIC);
  const [dateFrom, setDateFrom] = useState(urlRange.from || currentMonthRange().from);
  const [dateTo, setDateTo] = useState(urlRange.to || currentMonthRange().to);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [gatedOff, setGatedOff] = useState(false);
  // { lineId, tab } — one row open at a time, its own tab remembered.
  const [drill, setDrill] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [view, setView] = useState('categories');
  const coverageNoticeRef = useRef(null);

  // The period arrows fire one load per click and this endpoint is several
  // scans on a synchronous connection, so a slow abandoned response can land
  // after a newer one and repaint the page with a period the user already
  // left. That does not heal on its own and reads as a data bug.
  const loadToken = useRef(0);

  const setDateRange = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
    // Called only with a range computed just now (the picker, or the roll
    // below), so matching it against today's presets is sound here.
    const next = withPeriodParams(searchParams, activePresetKey(from, to, PRESETS_ANALYTIC), from, to);
    // replace: Back returns to /analysis rather than stepping through every
    // period the user tried on the way.
    setSearchParams(next, { replace: true });
  };
  // Rolls a preset forward when the tab comes back after the day changed.
  usePresetPeriod(dateFrom, dateTo, PRESETS_ANALYTIC, setDateRange);

  const fetchData = useCallback(() => {
    if (!budgetModuleEnabled) { setLoading(false); return; }
    const token = ++loadToken.current;
    setLoading(true);
    revenueApi.report({ date_from: dateFrom, date_to: dateTo })
      .then(r => {
        if (token !== loadToken.current) return;
        setReport(r.data);
        setLoadError(false);
        setGatedOff(false);
      })
      .catch(err => {
        if (token !== loadToken.current) return;
        // 403 is the module gate, not a failure — a different screen.
        if (err?.response?.status === 403) setGatedOff(true);
        else setLoadError(true);
      })
      .finally(() => { if (token === loadToken.current) setLoading(false); });
  }, [dateFrom, dateTo, budgetModuleEnabled]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const lines = useMemo(() => (report?.lines || []), [report]);
  const totals = report?.totals || null;
  // Spend no mapping rule reaches. It carries the same shape as a category and
  // COUNTS in the totals, so the rows on screen add up to the bottom line.
  const unmapped = report?.unmapped || null;

  // Opens on spend, biggest first — the order the הכנסות מול הוצאות CARD uses,
  // and this page is reached by clicking that card. Left unsorted, the rows
  // arrived in category order and the same six lines read in two different
  // orders one click apart, which makes the report look like different data
  // rather than more of it. Still fully sortable; this only sets where it opens.
  const { sorted, indicator, headerProps } = useSortable(lines, {
    name: l => l.category_line_name,
    spend: l => l.spend_net_total,
    pct: l => l.pct_of_revenue,
  }, { field: 'spend', dir: 'desc' });

  // Expanding a row IS the supplier split — there is no second thing it could
  // be any more. The monthly breakdown used to live in here behind a tab, and
  // it was in the wrong place twice over: at a single-month range (the default,
  // and the only range /analysis ever links here with) it rendered ONE row
  // restating the row that was just clicked, and even across many months a
  // trend inside an accordion cannot do its job, because only one category can
  // be open at a time and a trend is for COMPARING. So it moved up to a
  // table-level view where every category's months are on screen together.
  const toggleDrill = (lineId) => {
    setDrill(prev => (prev && prev.lineId === lineId ? null : { lineId }));
  };

  // The month columns, taken from the first line: the server computes the same
  // window for every line, so any of them carries the full list.
  const monthKeys = lines[0]?.months?.map(m => m.month) ?? [];
  const multiMonth = monthKeys.length > 1;
  // A single-month range has no trend to show, so the switch itself disappears
  // rather than offering a view that restates the table beside it.
  const showViewTabs = multiMonth;

  // The coverage caveat is real but it is NOT what the reader came for, and
  // above the tiles it spent the whole top of a phone screen before a single
  // figure (owner). It sits under the table now, with a marker by the title so
  // it is still announced where the eye lands first.
  const partialCoverage = Boolean(
    report?.totals && report.totals.missing_days_count > 0 && report.totals.has_revenue,
  );

  // "How far has revenue been entered" — the /analysis card's footer line,
  // same helper and wording ("*הוזנו הכנסות, עבור …"), at the foot of the page
  // right above the missing-days notice it explains (owner: a caption beside
  // the period picker did not sit well). Hidden on a historical range.
  const frontierLine = totals && entryFrontierLine({
    frontier: report.entry_frontier, hasRevenue: totals.has_revenue, dateTo, bannerShowing: partialCoverage,
    today: isoToday(),
  });

  const breadcrumb = (
    <div className="breadcrumb">
      <Link className="link" to="/analysis">ניתוח רכש</Link>
      <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
      <span>הכנסות מול הוצאות</span>
    </div>
  );

  if (!budgetModuleEnabled || gatedOff) {
    // A card may vanish when the module is off; a page somebody navigated to
    // may not — a blank screen reads as a broken link.
    return (
      <div>
        {breadcrumb}
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 8 }}>
            מודול ההוצאות וההכנסות אינו פעיל למסעדה זו
          </div>
          <div style={{ fontSize: '0.88rem', color: 'var(--ink-muted)' }}>
            הפעלה דרך הגדרות המערכת (אדמין בלבד)
          </div>
        </div>
      </div>
    );
  }

  const exportOptions = [
    {
      key: 'report',
      label: 'דוח הכנסות מול הוצאות',
      hint: 'קטגוריות, פירוק חודשי ופירוק לפי ספקים',
      run: () => {
        track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'revenue_report' });
        return revenueApi.exportReport({ date_from: dateFrom, date_to: dateTo });
      },
    },
    {
      key: 'items',
      label: 'דוח הכנסות והוצאות ברמת פריט',
      hint: 'שורה לכל פריט, עם הקטגוריה ומקור השיוך - קובץ גדול',
      run: () => {
        track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'revenue_report_items' });
        return revenueApi.exportReportItems({ date_from: dateFrom, date_to: dateTo });
      },
    },
  ];

  const header = (
    <>
      {breadcrumb}
      <div className="page-title-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {/* Replaces the breadcrumb on a phone (CSS hides each in the other's
              breakpoint). A two-level trail carries exactly what one back
              control carries, and this one is a 40x40 target rather than a
              13px text link. */}
          <Link className="report-back" to="/analysis" aria-label="חזרה לניתוח רכש">
            <Icon name="chevron" size={20} style={{ transform: 'rotate(-90deg)' }} />
          </Link>
          <h1 className="page-title">הכנסות מול הוצאות</h1>
          {report?.hidden_hq && <HqOnlyNotice count={report.hidden_hq.supplier_count} amount={report.hidden_hq.amount} pending={loading} basis="הוצאות בימים שנספרו (ללא מע״מ)" />}
        </div>
        {/* [דוחות] [⋮] — the same action cluster the other report pages close
            on, so the ⋮ lands where a reader already looks for "everything
            else about this page". */}
        <div className="page-title-actions">
          <ExportMenu scopeLabel={periodLabel(dateFrom, dateTo)} options={exportOptions} />
          {/* The two explainers this page used to carry as banners. A permanent
              yellow box under the table read as a WARNING about the numbers
              beside it, when it is only an explanation of how they are built —
              so it moved in here, where a reader goes when something does not
              add up, and the page keeps its one real warning (missing פדיון
              days, which is actionable) for the moment it actually applies. */}
          <div className="page-menu">
            <button
              className="page-menu-trigger"
              aria-label="על הדוח"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(o => !o)}
            >⋮</button>
            {menuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
                <div className="page-menu-panel" style={{ minWidth: 300, maxWidth: 340, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>איך מחושב</div>
                  <RevenueCalculationExplanation />
                  <ExcludedSpendNote
                    totals={totals}
                    style={{ borderTop: '1px solid #eef2f7', margin: '10px 0 0', padding: '10px 0 0' }}
                  />

                  <div style={{ borderTop: '1px solid #eef2f7', margin: '10px 0 0', padding: '10px 0 0' }}>
                    <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>
                      למה המספרים לא תמיד מסתדרים
                    </div>
                    <p style={{ margin: 0, fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                      בפירוק לפי ספקים מוצג רק החלק מהרכש של הספק ששייך לקטגוריה.
                      סיווג פריט גובר על שיוך הספק, ולכן ספק אחד יכול להופיע בכמה
                      קטגוריות, והמספרים כאן לא יתאימו לדוח "רכש לפי ספקים".
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                      סה״כ ההוצאות כאן יכול להיות נמוך מ"סה״כ רכש" בניתוח רכש:
                      רכש מימים שלא הוזנה להם הכנסה לא נכלל עד שתוזן, ורכש ללא שיוך לא נכלל בהוצאות.
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                      יום סגירה לא נחשב יום חסר, וההוצאות שנרשמו בו נכללות.
                    </p>
                  </div>
                  <ViewAsMenuItems onDone={() => setMenuOpen(false)} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Desktop: pinned to the far (left) end, where the sibling reports'
          filter bars park their date control (owner). This page has no filter
          bar, so the flex row does what .docs-filter-bar's auto margin does. */}
      <div
        className={isMobile ? 'period-bar' : 'docs-filter-wrap'}
        style={isMobile ? { marginBottom: 16 } : { marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}
      >
        <PeriodPicker
          dateFrom={dateFrom} dateTo={dateTo}
          onChange={setDateRange}
          presets={PRESETS_ANALYTIC}
          variant={isMobile ? 'bar' : 'pill'}
        />
      </div>
    </>
  );

  if (loading) {
    return <div>{header}<div className="loading-container"><div className="spinner" /></div></div>;
  }
  if (loadError) {
    return (
      <div>{header}
        <InlineLoadError message="לא ניתן לטעון את דוח ההכנסות מול ההוצאות" onRetry={fetchData} />
      </div>
    );
  }
  if (lines.length === 0) {
    return (
      <div>{header}
        <div className="card">
          <EmptyState message="עדיין לא הוגדרו קטגוריות">
            <p className="empty-subline">
              הגדירו קטגוריות ושייכו אליהם רכש - ואז יופיע כאן היחס בין ההוצאות להכנסות.
            </p>
            <Link className="btn btn-primary" to="/categories-management" style={{ marginTop: 16 }}>
              להגדרת קטגוריות
            </Link>
          </EmptyState>
        </div>
      </div>
    );
  }

  const anyRevenue = Boolean(totals && totals.has_revenue);

  return (
    <div className="revenue-report-page">
      {header}


      {!anyRevenue && (
        <div className="card" style={{ marginBottom: 16, padding: '14px 16px', fontSize: '0.88rem', color: '#7a5c1f', background: '#fffbeb' }}>
          עדיין לא הוזן פדיון לטווח הזה, כך שאין ממה לחשב אחוזים. ההוצאות למטה כוללות רק ימי סגירה - רכש משאר הימים ייכלל אחרי הזנת הפדיון.
          {' '}<Link className="link" to="/categories-management?revenue=1">להזנת פדיון</Link>
        </div>
      )}

      {/* Revenue and coverage state ONCE, here. They are restaurant-level facts:
          repeating revenue down a per-category column (as this table used to)
          reads as each category having revenue of its own, which is exactly the
          model this report was rebuilt to remove. */}
      {totals && anyRevenue && (
        <div className="card" style={{ marginBottom: 16 }}>
          {/* Shared three-tile header; missing-day navigation remains below it
              after removal of the aggregate percentage tile. */}
          <RevenueMetricTiles
            totals={totals}
            // Matched to the card's tiles deliberately: the whole point of
            // sharing this component is that moving between the card and the
            // report costs no re-reading, which a different precision on the
            // same three figures would undo.
            money={formatCurrency}
            showGross
          />
          {partialCoverage && (
              <button
                type="button"
                onClick={() => coverageNoticeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                style={{
                  marginTop: 8, padding: 0, background: 'none', border: 0, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600,
                  color: '#B45309', textDecoration: 'underline',
                }}
              >
                חסרים ימי דיווח
              </button>
          )}
        </div>
      )}

      <div className="card">
        {/* Docked to the top of the data card, the same .view-tabs grammar
            /suppliers and /credits use — folder tabs on desktop, a segmented
            pill on a phone. State, not routes: this switches a panel in place
            and changes no URL, so the tabs are aria-pressed toggle buttons
            rather than <Link>s (and rather than role="tab", which would owe
            the reader arrow-key navigation this does not implement). */}
        {showViewTabs && (
          <nav className="view-tabs" aria-label="תצוגת הדוח">
            <button
              type="button"
              className={`view-tab${view === 'categories' ? ' is-active' : ''}`}
              aria-pressed={view === 'categories'}
              onClick={() => setView('categories')}
            >
              לפי קטגוריה
            </button>
            <button
              type="button"
              className={`view-tab${view === 'months' ? ' is-active' : ''}`}
              aria-pressed={view === 'months'}
              onClick={() => setView('months')}
            >
              פירוק חודשי
            </button>
          </nav>
        )}

        {showViewTabs && view === 'months' ? (
          <MonthlyMatrix lines={sorted} unmapped={unmapped} monthKeys={monthKeys} foodCostMonths={report.months?.food_cost || []} />
        ) : (
        <div className="table-wrapper table-scroll-mobile revenue-lines-table">
          <table>
            <thead>
              <tr>
                {/* Three columns. The per-category הכנסות column is gone (one
                    denominator, stated above), and so is יעד % מההכנסות — a ₪
                    budget measured against spend is רכש מול יעדי תקציב's
                    question, not this one's (owner, 2026-08-28). כיסוי moved to
                    the header block for the same reason: it is one fact about
                    the restaurant, not one per row. */}
                {/* Short labels, with the VAT basis on its own line rather than
                    inside the label: three columns have to fit 319px of card
                    without a sideways scroll, and "(ללא מע״מ)" inline was most
                    of the 17px that did not. The % column drops its
                    "מתוך הכנסות התקופה" subline entirely — the metrics card
                    directly above names the denominator, and the ⋮ menu
                    explains it in full. */}
                <th {...headerProps('name')}>קטגוריה {indicator('name')}</th>
                <th {...headerProps('spend')}>
                  הוצאות {indicator('spend')}
                  <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11, display: 'block' }}>
                    ללא מע״מ
                  </span>
                </th>
                <th {...headerProps('pct')}>% מההכנסות {indicator('pct')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((l) => {
                const open = drill?.lineId === l.category_line_id;
                return (
                  <Fragment key={l.category_line_id}>
                    <tr
                      className="row-clickable"
                      onClick={() => toggleDrill(l.category_line_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <ExpandCaret open={open} />
                          <span style={{
                            width: 10, height: 10, borderRadius: 3,
                            background: l.color || '#cbd5e1', display: 'inline-block', flex: '0 0 auto',
                          }} />
                          <span>{l.category_line_name}</span>{l.is_food_cost && <FoodCostIcon />}
                        </span>
                      </td>
                      <td>{formatCurrencyWhole(l.spend_net_total)}</td>
                      <td style={{ fontWeight: 700 }}>{pct(l.pct_of_revenue)}</td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={COLS} style={{ background: '#fafbfc' }}>
                          {/* A named landmark, so the panel is addressable on
                              its own — it used to be the drill nav's
                              aria-label, which went with the tab bar. */}
                          <section aria-label={`ספקי ${l.category_line_name}`}>
                            <SuppliersTable line={l} />
                          </section>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}

              {/* Not a rank participant — it is the measure of what the ranking
                  does not cover, so it is pinned and never sorted in. */}
              {/* Non-zero, not positive — a counted credit can leave it
                  negative, and it still counts in the tiles above. */}
              {unmapped && unmapped.spend_net_total !== 0 && (
                <tr className="revenue-unmapped-row">
                  <td>
                    <Link className="link" style={{ color: 'inherit' }} to="/categories-management">הוצאות ללא שיוך</Link>
                    <span className="unmapped-outside-total">לא נכלל בהוצאות</span>
                  </td>
                  <td>{formatCurrencyWhole(unmapped.spend_net_total)}</td>
                  <td style={{ fontWeight: 700 }}>{pct(unmapped.pct_of_revenue)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {frontierLine && (
        <div style={{ marginTop: 16 }}>
          <EntryFrontierText line={frontierLine} style={{ fontSize: 12.5 }} />
        </div>
      )}

      {partialCoverage && (
        <div
          ref={coverageNoticeRef}
          className="card"
          style={{ marginTop: frontierLine ? 8 : 16, padding: '14px 16px', fontSize: '0.88rem', color: '#7a5c1f', background: '#fffbeb' }}
        >
          {/* Says who did not happen (no revenue entered), then the
              consequence, in one sentence — "חסרים N ימי דיווח - הוצאות מימים
              אלה…" read as two fragments glued with a dash (owner). */}
          {totals.missing_days_count === 1
            ? 'ליום אחד בטווח לא הוזנו הכנסות, ולכן ההוצאות שלו לא נכללו בחישוב.'
            : `ל-${totals.missing_days_count} ימים בטווח לא הוזנו הכנסות, ולכן ההוצאות שלהם לא נכללו בחישוב.`}
          {' '}<Link className="link" to="/categories-management?revenue=1">להזנת הכנסות</Link>
        </div>
      )}
    </div>
  );
}

// Every category's months, side by side — the view the old per-row tab could
// never be. Rows are categories, columns are months, and the cell is the % of
// that month's revenue, so a trend reads ACROSS a row and two categories'
// trends compare DOWN a column. The ₪ behind each cell rides in its title
// rather than a second line: doubling the cell count would make the matrix
// unreadable at the width a phone has.
function MonthlyMatrix({ lines, unmapped, monthKeys, foodCostMonths }) {
  const byMonth = (l) => new Map((l.months || []).map(m => [m.month, m]));
  const partial = new Set();
  for (const m of lines[0]?.months || []) if (m.is_partial) partial.add(m.month);

  const cell = (m) => {
    if (!m) return <span style={{ color: '#cbd5e1' }}>-</span>;
    if (m.pct_of_revenue == null) return <span style={{ color: '#cbd5e1' }}>-</span>;
    return (
      <span title={`${formatCurrencyWhole(m.spend_net_total)} (ללא מע״מ)`} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {m.pct_of_revenue}%
      </span>
    );
  };

  const row = (name, color, months, muted, isFoodCost = false) => {
    const map = byMonth({ months });
    return (
      <tr key={name} className={muted ? 'revenue-unmapped-row' : undefined}>
        <td>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 10, height: 10, borderRadius: 3,
              background: color || '#cbd5e1', display: 'inline-block', flex: '0 0 auto',
            }} />
            <span>{name}</span>{isFoodCost && <FoodCostIcon />}
          </span>
        </td>
        {monthKeys.map(k => <td key={k}>{cell(map.get(k))}</td>)}
      </tr>
    );
  };

  return (
    <div className="table-wrapper table-scroll-mobile">
      <table>
        <thead>
          <tr>
            <th>קטגוריה</th>
            {monthKeys.map(k => (
              <th key={k}>
                {formatIsoMonth(k)}
                {/* A three-day August against a whole July is not a trend. */}
                {partial.has(k) && (
                  <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11, display: 'block' }}>
                    חודש חלקי
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map(l => row(l.category_line_name, l.color, l.months, false, l.is_food_cost))}
          {row('Food Cost', 'var(--ink-muted)', foodCostMonths)}
          {unmapped && unmapped.spend_net_total !== 0 && row('ללא שיוך — לא נכלל בהוצאות', '#cbd5e1', unmapped.months, true)}
        </tbody>
      </table>
      <p style={{ margin: '10px 0 0', fontSize: 11.5, color: '#94a3b8' }}>
        האחוז הוא ההוצאות של הקטגוריה בימים שנספרו מתוך ההכנסות של אותו חודש.
      </p>
    </div>
  );
}


function SuppliersTable({ line }) {
  return (
    <div>
      {!line.suppliers?.length ? (
        <p style={{ color: '#94a3b8' }}>אין הוצאות משויכות לקטגוריה הזו בימים שנספרו.</p>
      ) : (
        <div className="table-wrapper table-scroll-mobile">
          <table>
            <thead>
              <tr>
                <th>ספק</th>
                <th>הוצאות (ללא מע״מ)</th>
                <th>% מהקטגוריה</th>
                <th>מספר מסמכים</th>
                {/* Last, not second (owner): it explains WHY a supplier is
                    here, which matters less than how much. */}
                <th>מקור השיוך</th>
              </tr>
            </thead>
            <tbody>
              {line.suppliers.map(s => (
                <tr key={s.supplier_id}>
                  <td>{s.supplier_name}</td>
                  <td>{formatCurrencyWhole(s.spend_net)}</td>
                  <td>{pct(s.pct_of_line)}</td>
                  <td>{s.doc_count}</td>
                  <td>
                    <span
                      className="badge badge-gray"
                      title={s.source === 'mixed' && s.by_source?.length
                        ? s.by_source.map(b => `${sourceLabel(b.source)}: ${formatCurrencyWhole(b.spend_net)}`).join(' · ')
                        : undefined}
                    >
                      {sourceLabel(s.source)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
